/**
 * PlaceholderApp.jsx - Shell Design System v5.0
 *
 * This is the default landing page for new shells.
 * It demonstrates the design system components and can be easily replaced.
 *
 * TO CUSTOMIZE:
 * 1. Edit this file directly, or
 * 2. Replace it entirely with your own App component
 * 3. Update App.jsx to import your component instead
 *
 * DESIGN SYSTEM USAGE:
 * - Components: import from './shell-design-system'
 * - Tokens: use CSS variables like var(--space-4), var(--text-primary)
 * - Icons: import from 'iconoir-react'
 *
 * See /arc/design-system for full component documentation
 */

import React, { useState, useEffect } from 'react';
import {
  MinimalBackgroundSystem,
  GlassContainer,
  DotMatrix,
  StatusIndicator,
  Button
} from './shell-design-system';
import { Server, Settings, Terminal } from 'iconoir-react';

function PlaceholderApp() {
  // Extract shell name from hostname (e.g., "myshell.sygna.org" -> "myshell")
  const hostname = window.location.hostname;
  const shellName = hostname.split('.')[0] || 'shell';

  // Track session uptime
  const [uptime, setUptime] = useState(0);
  const [status, setStatus] = useState('online');

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const seconds = Math.floor((Date.now() - startTime) / 1000);
      setUptime(seconds);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Format uptime as HH:MM
  const formatUptime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  return (
    <MinimalBackgroundSystem showGrid={true}>
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)'
      }}>
        <GlassContainer variant="large">
          <div style={{
            textAlign: 'center',
            padding: 'var(--space-8)',
            maxWidth: '500px'
          }}>

            {/* Shell Name - Main heading */}
            <h1 style={{
              fontSize: 'var(--text-4xl)',
              fontWeight: '900',
              fontFamily: 'var(--font-mono)',
              textTransform: 'uppercase',
              letterSpacing: '-0.02em',
              marginBottom: 'var(--space-6)',
              color: 'var(--text-primary)'
            }}>
              {shellName}
            </h1>

            {/* Status Indicator */}
            <div style={{ marginBottom: 'var(--space-8)' }}>
              <StatusIndicator status={status} size="large" showText={true} />
            </div>

            {/* Metrics Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 'var(--space-6)',
              marginBottom: 'var(--space-8)'
            }}>
              <div style={{
                padding: 'var(--space-4)',
                backgroundColor: 'var(--surface-secondary)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--surface-border)'
              }}>
                <DotMatrix value={formatUptime(uptime)} size="medium" />
                <p style={{
                  marginTop: 'var(--space-2)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontFamily: 'var(--font-mono)'
                }}>
                  Session Uptime
                </p>
              </div>

              <div style={{
                padding: 'var(--space-4)',
                backgroundColor: 'var(--surface-secondary)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--surface-border)'
              }}>
                <DotMatrix value="READY" size="medium" />
                <p style={{
                  marginTop: 'var(--space-2)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontFamily: 'var(--font-mono)'
                }}>
                  System Status
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                className="btn btn--primary"
                onClick={() => window.location.href = '/api/docs'}
              >
                <Terminal width={16} height={16} style={{ marginRight: 'var(--space-2)' }} />
                API Docs
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => window.location.href = '/arc/design-system'}
              >
                <Settings width={16} height={16} style={{ marginRight: 'var(--space-2)' }} />
                Design System
              </button>
            </div>

            {/* Footer hint */}
            <p style={{
              marginTop: 'var(--space-8)',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-mono)'
            }}>
              Edit <code style={{ color: 'var(--ansi-cyan)' }}>src/PlaceholderApp.jsx</code> to customize
            </p>
          </div>
        </GlassContainer>
      </div>
    </MinimalBackgroundSystem>
  );
}

export default PlaceholderApp;
